import type { ReservationWrapper } from "@b2bportal/core-types";
import { handleAxiosError } from "@b2bportal/core-utilities";
import { Product } from "@b2bportal/purchase-api";
import {
  type ActivityReservation,
  ItineraryState,
  type SingleItineraryRequest,
  type SingleItineraryResponse,
} from "@b2bportal/rbc-activities-api";
import { activitiesApi } from "@overrides/utilities";
import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";

export interface IActivityItineraryResponse {
  itineraries: {
    [key: string]: Array<ReservationWrapper<ActivityReservation>>;
  };
}

export const activitiesPostBookingApi = createApi({
  reducerPath: "activitiesPostBooking",
  baseQuery: fakeBaseQuery(),
  tagTypes: ["ActivityReservationList"],
  endpoints: (builder) => ({
    fetchAllActivityReservations: builder.query<
      IActivityItineraryResponse,
      void
    >({
      queryFn: async () => {
        try {
          const response = await activitiesApi.apiV0ItineraryActivitiesPut({
            states: [
              ItineraryState.Canceled,
              ItineraryState.Future,
              ItineraryState.Present,
              ItineraryState.Past,
            ],
          });

          const rawItineraries = response.data.itineraries;

          /**
           * Maps the API’s itinerary state key to our canonical ItineraryState value.
           * Handles casing (e.g. "future" → "Future") and spelling (e.g. "cancelled" → "Canceled")
           * so we can reliably use data.itineraries[ItineraryState.Future] etc. in the hook.
           */
          const normalizeItineraryStateKeyFromApi = (
            apiKey: string
          ): string => {
            const lower = apiKey.toLowerCase();
            if (lower === "canceled" || lower === "cancelled")
              return ItineraryState.Canceled;
            if (lower === "future") return ItineraryState.Future;
            if (lower === "present") return ItineraryState.Present;
            if (lower === "past") return ItineraryState.Past;
            return apiKey;
          };

          const wrappedReservations = Object.keys(rawItineraries).reduce(
            (acc, key) => {
              const items = rawItineraries[key];
              const canonicalKey = normalizeItineraryStateKeyFromApi(key);
              const isPast =
                canonicalKey === ItineraryState.Past ||
                canonicalKey === ItineraryState.Canceled;
              const wrapped = items.map((item: SingleItineraryResponse) => ({
                reservation: item.reservation,
                reservationId: item.reservation.customerReservationId,
                sortDate: item.reservation.activitySelection?.travelDate ?? "",
                recentFirst: isPast,
                isCancelled: canonicalKey === ItineraryState.Canceled,
                productType: Product.Activities,
              }));
              const existing = acc[canonicalKey] ?? [];
              return { ...acc, [canonicalKey]: [...existing, ...wrapped] };
            },
            {} as {
              [key: string]: Array<ReservationWrapper<ActivityReservation>>;
            }
          );

          return {
            data: {
              itineraries: wrappedReservations,
            },
          };
        } catch (error) {
          return handleAxiosError(error);
        }
      },
      providesTags: ["ActivityReservationList"],
    }),
    fetchActivityReservation: builder.query<
      SingleItineraryResponse,
      SingleItineraryRequest
    >({
      queryFn: async (request) => {
        try {
          const response =
            await activitiesApi.apiV0ItineraryActivitiesSinglePut(request);
          return { data: response.data };
        } catch (error) {
          return handleAxiosError(error);
        }
      },
    }),
  }),
});
